// Dates and numbers by locale. "Days" belong to the tenant's timezone, whatever the browser's is.
import type { UiLang } from "@imagora/contracts";

const LOCALES: Record<UiLang, string> = { ro: "ro-RO", bg: "bg-BG", hu: "hu-HU", en: "en-GB" };

export const localeOf = (lang: UiLang): string => LOCALES[lang];

export function formatDate(iso: string, lang: UiLang, timeZone: string): string {
  return new Intl.DateTimeFormat(localeOf(lang), { day: "numeric", month: "short", year: "numeric", timeZone }).format(new Date(iso));
}

export function formatDateTime(iso: string, lang: UiLang, timeZone: string): string {
  return new Intl.DateTimeFormat(localeOf(lang), { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone }).format(new Date(iso));
}

export function formatLongDate(date: Date, lang: UiLang, timeZone: string): string {
  return new Intl.DateTimeFormat(localeOf(lang), { weekday: "long", day: "numeric", month: "long", timeZone }).format(date);
}

/** A date picked in a calendar is a plain calendar day, so it is shown without a timezone shift. */
export function formatDay(date: Date, lang: UiLang): string {
  return new Intl.DateTimeFormat(localeOf(lang), { day: "numeric", month: "short", year: "numeric" }).format(date);
}

export function formatNumber(value: number, lang: UiLang): string {
  return new Intl.NumberFormat(localeOf(lang)).format(value);
}

const RELATIVE_STEPS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 86400],
  ["month", 30 * 86400],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

export function formatRelative(iso: string, lang: UiLang, nowMs: number): string {
  const seconds = (new Date(iso).getTime() - nowMs) / 1000;
  const format = new Intl.RelativeTimeFormat(localeOf(lang), { numeric: "auto" });
  for (const [unit, size] of RELATIVE_STEPS) {
    if (Math.abs(seconds) >= size) return format.format(Math.trunc(seconds / size), unit);
  }
  return format.format(0, "minute");
}

/** The UTC instant of midnight, at the start of a calendar day, in a timezone. */
function zonedMidnight(year: number, month: number, day: number, timeZone: string): Date {
  const guess = Date.UTC(year, month, day);
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" }).formatToParts(new Date(guess));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"), part("second"));
  return new Date(guess - (asUtc - guess));
}

export interface DayRange {
  from?: Date;
  to?: Date;
}

/** A picked period as the API wants it: from the first midnight, up to but excluding the midnight after the last day. */
export function dateRangeToQuery(range: DayRange | undefined, timeZone: string): { from?: string; to?: string } {
  if (!range?.from) return {};
  const last = range.to ?? range.from;
  return {
    from: zonedMidnight(range.from.getFullYear(), range.from.getMonth(), range.from.getDate(), timeZone).toISOString(),
    to: zonedMidnight(last.getFullYear(), last.getMonth(), last.getDate() + 1, timeZone).toISOString(),
  };
}
